// frontend/src/pages/SharedVoice.js

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Container,
  Card,
  CardContent,
  IconButton,
  Chip,
  Button
} from '@mui/material';
import {
  VolumeUp as VoiceIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  FileCopy as CopyIcon,
  Schedule as TimeIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import axios from '../api/axiosConfig';

const SharedVoice = () => {
  const { token } = useParams();
  const [voiceNote, setVoiceNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState(null);
  const [refreshingAudio, setRefreshingAudio] = useState(false);

  useEffect(() => {
    const fetchSharedVoice = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/shared/voice/${token}`);
        setVoiceNote(response.data.voiceNote);
      } catch (error) {
        console.error('Error fetching shared voice note:', error);
        setError(error.response?.data?.error || 'Failed to load shared voice note');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchSharedVoice();
    }
  }, [token]);

  // Refresh audio token when it expires
  const refreshAudioToken = async () => {
    if (refreshingAudio) return; // Prevent multiple simultaneous refreshes

    try {
      setRefreshingAudio(true);
      console.log('[SharedVoice] Refreshing audio token...');

      const response = await axios.post(`/api/shared/voice/${token}/refresh-audio`);
      const { audioSignedUrl } = response.data;

      // Update voiceNote with new signed URL
      setVoiceNote(prev => ({
        ...prev,
        audioSignedUrl: audioSignedUrl
      }));

      console.log('[SharedVoice] Audio token refreshed successfully');
      return audioSignedUrl;
    } catch (error) {
      console.error('[SharedVoice] Failed to refresh audio token:', error);
      throw error;
    } finally {
      setRefreshingAudio(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle audio playback
  const handlePlayAudio = async () => {
    // Prefer signed URL for public playback, fallback to audioUrl for legacy
    const audioSrc = voiceNote?.audioSignedUrl || voiceNote?.audioUrl;
    if (!audioSrc) return;

    if (isPlaying) {
      // Stop playing
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
      setIsPlaying(false);
    } else {
      // Start playing
      if (!audioElement && audioSrc) {
        const audio = new Audio(audioSrc);
        audio.addEventListener('ended', () => setIsPlaying(false));
        audio.addEventListener('error', async (e) => {
          console.error('Audio playback error:', e);
          console.error('Failed URL:', audioSrc);
          setIsPlaying(false);

          // Auto-refresh token on playback error (likely expired)
          try {
            console.log('[SharedVoice] Attempting to refresh expired audio token...');
            const newUrl = await refreshAudioToken();

            // Retry playback with new URL
            if (newUrl) {
              console.log('[SharedVoice] Retrying playback with refreshed token');
              const retryAudio = new Audio(newUrl);
              retryAudio.addEventListener('ended', () => setIsPlaying(false));
              retryAudio.addEventListener('error', (retryError) => {
                console.error('[SharedVoice] Playback failed after token refresh:', retryError);
                setIsPlaying(false);
              });
              setAudioElement(retryAudio);
              retryAudio.play();
              setIsPlaying(true);
            }
          } catch (refreshError) {
            console.error('[SharedVoice] Failed to recover from audio error:', refreshError);
          }
        });
        setAudioElement(audio);
        audio.play();
      } else if (audioElement) {
        audioElement.play();
      }
      setIsPlaying(true);
    }
  };

  // Handle copy transcript
  const handleCopyTranscript = async () => {
    if (!voiceNote?.transcript) return;

    try {
      await navigator.clipboard.writeText(voiceNote.transcript);
      // Could show a toast notification here
      console.log('Transcript copied to clipboard');
    } catch (err) {
      console.error('Failed to copy transcript:', err);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          gap: 2 
        }}>
          <CircularProgress size={48} />
          <Typography variant="h6" color="text.secondary">
            Loading shared voice note...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert 
          severity="error" 
          sx={{ 
            borderRadius: 2,
            '& .MuiAlert-message': {
              fontSize: '1.1rem'
            }
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (!voiceNote) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="info">
          Voice note not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
          <PublicIcon color="primary" />
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Shared Voice Recording
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          This voice recording has been shared publicly
        </Typography>
      </Box>

      {/* Main Voice Note Card */}
      <Card sx={{ 
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        border: '1px solid',
        borderColor: 'divider'
      }}>
        <CardContent sx={{ p: 4 }}>
          {/* Voice Note Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 3 }}>
            <Paper sx={{ 
              p: 2, 
              bgcolor: 'primary.light',
              color: 'primary.main',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center'
            }}>
              <VoiceIcon sx={{ fontSize: 32 }} />
            </Paper>
            
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {voiceNote.title}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TimeIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(voiceNote.createdAt)}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Duration: {voiceNote.duration}
                </Typography>
                <Chip 
                  label={voiceNote.noteContentType}
                  size="small"
                  color={voiceNote.noteContentType === 'mixed' ? 'primary' : 'default'}
                  variant="outlined"
                />
              </Box>

              {/* Audio Controls */}
              {voiceNote.hasAudio && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={isPlaying ? <PauseIcon /> : <PlayIcon />}
                    onClick={handlePlayAudio}
                    size="large"
                    sx={{ minWidth: 140 }}
                  >
                    {isPlaying ? 'Pause Audio' : 'Play Audio'}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          {/* Transcript */}
          {voiceNote.transcript && (
            <>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Transcript
              </Typography>
              
              <Paper sx={{ 
                p: 3, 
                bgcolor: 'grey.50',
                borderRadius: 2,
                mb: 3,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  }}
                >
                  {voiceNote.transcript}
                </Typography>
              </Paper>

              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={handleCopyTranscript}
                  size="large"
                >
                  Copy Transcript
                </Button>
              </Box>
            </>
          )}

          {!voiceNote.transcript && (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              color: 'text.secondary'
            }}>
              <VoiceIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6">
                Audio Only
              </Typography>
              <Typography variant="body2">
                This voice recording doesn't have a transcript available.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Footer */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Shared via <strong>Blackmount</strong> - Your AI growth companion
        </Typography>
      </Box>
    </Container>
  );
};

export default SharedVoice;